import { z } from 'zod';

function BoilmasterSheetRow<T extends z.ZodTypeAny>(fieldsShape: T) {
  return z.object({
    fields: fieldsShape,
    row_id: z.number().int().min(0),
    sheet: z.string(),
  });
}

const BoilmasterIcon = z.object({
  id: z.number().int(),
  path: z.string(),
  path_hr1: z.string(),
});

type BoilmasterIcon = z.TypeOf<typeof BoilmasterIcon>;

const BoilmasterItemSearchResult = z.object({
  Icon: BoilmasterIcon,
  ItemSearchCategory: BoilmasterSheetRow(
    z.object({
      Name: z.string(),
    })
  ),
  LevelItem: BoilmasterSheetRow(z.object({})),
  Rarity: z.number().int(),
  Name: z.string(),
});

const BoilmasterItemSearchResults = z.object({
  next: z.string().optional(),
  results: z.array(
    BoilmasterSheetRow(BoilmasterItemSearchResult).merge(
      z.object({
        score: z.number(),
      })
    )
  ),
  schema: z.string(),
});

const TcItemSearchResult = z.object({
  id: z.number(),
  icon: z.string(),
  itemKind: z.string(),
  itemSearchCategory: z.object({
    id: z.number(),
    name: z.string(),
  }),
  levelItem: z.number(),
  name: z.string(),
  rarity: z.number(),
});

const TcItemSearchResults = z.object({
  total: z.number(),
  items: z.array(TcItemSearchResult),
});

export type BoilmasterItemSearchResults = z.TypeOf<typeof BoilmasterItemSearchResults>;

interface XIVAPISearchResults {
  Pagination: {
    Results: number;
    ResultsTotal: number;
  };
  Results: {
    ID: number;
    Icon: string;
    ItemKind: {
      Name: string;
    };
    ItemSearchCategory: {
      ID: number;
      Name: string;
    };
    LevelItem: number;
    Name: string;
    Rarity: number;
  }[];
}

export interface ItemSearchResults {
  resultsReturned: number;
  resultsTotal: number;
  results: SearchItem[];
}

export interface SearchItem {
  id: number;
  icon: string;
  itemKind: string;
  itemSearchCategory: {
    id: number;
    name: string;
  };
  levelItem: number;
  name: string;
  rarity: number;
}

function getRepositoryUrl(lang: string) {
  if (lang === 'ko') {
    return 'https://lalafell-api.universalis.app/api';
  } else {
    throw new Error(`Out of range: ${lang}`);
  }
}

export async function searchItemsV1(
  query: string,
  lang: string,
  algorithm?: string,
  abort?: AbortController
): Promise<ItemSearchResults> {
  const baseUrl = getRepositoryUrl(lang);

  let searchUrl = `${baseUrl}/search?string=${query}&indexes=item&language=${lang}&filters=ItemSearchCategory.ID>=1&columns=ID,Icon,Name,LevelItem,Rarity,ItemSearchCategory.Name,ItemSearchCategory.ID,ItemKind.Name&limit=100&sort_field=LevelItem&sort_order=desc`;
  if (algorithm != null) {
    searchUrl += `&string_algo=${algorithm}`;
  }

  const data: XIVAPISearchResults = await fetch(searchUrl, {
    signal: abort?.signal,
  }).then((res) => res.json());

  return {
    resultsReturned: data.Pagination.Results,
    resultsTotal: data.Pagination.ResultsTotal,
    results: data.Results.map((item) => ({
      id: item.ID,
      icon: `https://xivapi.com${item.Icon}`,
      itemKind: item.ItemKind.Name,
      itemSearchCategory: {
        id: item.ItemSearchCategory.ID,
        name: item.ItemSearchCategory.Name,
      },
      levelItem: item.LevelItem,
      name: item.Name,
      rarity: item.Rarity,
    })),
  };
}

export async function searchItemsTc(
  query: string,
  lang: string,
  abort?: AbortController
): Promise<ItemSearchResults> {
  const params = new URLSearchParams({
    sheets: 'Items',
    query: query,
    language: lang,
    limit: '100',
    field: 'Name,ItemSearchCategory.Name,Icon,LevelItem.todo,Rarity',
  });

  const searchUrl = `https://tc-ffxiv-item-search-service.onrender.com/items/search?${params.toString()}`;
  const data = await fetch(searchUrl, {
    signal: abort?.signal,
  })
    .then((res) => res.json())
    .then((res) => TcItemSearchResults.parse(res))
    .catch((err) => {
      if (err instanceof z.ZodError) {
        console.error('Failed to parse TC search results:', err.message);
      }
      throw err;
    });

  return {
    resultsReturned: data.items.length,
    resultsTotal: data.items.length,
    results: data.items
      .map((result) => ({
        id: result.id,
        icon: `https://v2.xivapi.com/api/asset/${result.icon}?format=png`,
        itemKind: result.itemKind,
        itemSearchCategory: {
          id: result.itemSearchCategory.id,
          name: result.itemSearchCategory.name,
        },
        levelItem: result.levelItem,
        name: result.name,
        rarity: result.rarity,
      }))
      .sort((a, b) => b.levelItem - a.levelItem),
  };
}

function iconUrlV2(icon: BoilmasterIcon): string {
  return `https://v2.xivapi.com/api/asset/${icon.path}?format=png`;
}

export async function searchItemsV2(
  query: string,
  lang: string,
  abort?: AbortController
): Promise<ItemSearchResults> {
  const baseUrl = lang === 'chs' ? 'https://xivapi-v2.xivcdn.com/api' : 'https://v2.xivapi.com/api';
  const searchUrl = `${baseUrl}/search`;
  const keywords = query
    .split(' ')
    .map((term) => `+Name~"${term}"`)
    .join(' ');
  const params = new URLSearchParams({
    sheets: 'Item',
    query: `${keywords} +ItemSearchCategory>=1`,
    language: lang,
    limit: '60',
    // LevelItem.todo is a nonexistent field, but using that causes an empty fields
    // property to be returned, which is useful for reducing the response size.
    // TODO: use @raw once it exists
    fields: 'Name,ItemSearchCategory.Name,Icon,LevelItem.todo,Rarity',
  });

  const data = await fetch(`${searchUrl}?${params.toString()}`, {
    signal: abort?.signal,
  })
    .then((res) => res.json())
    .then((res) => BoilmasterItemSearchResults.parse(res))
    .catch((err) => {
      if (err instanceof z.ZodError) {
        console.error('Failed to parse search results:', err.message);
      }

      throw err;
    });

  return {
    resultsReturned: data.results.length,
    resultsTotal: data.results.length,
    results: data.results
      .filter((result) => result.fields.ItemSearchCategory.row_id >= 1)
      .map((result) => ({
        id: result.row_id,
        icon:
          lang === 'chs'
            ? `${baseUrl}/asset?path=${result.fields.Icon.path}&format=png`
            : iconUrlV2(result.fields.Icon),
        itemKind: result.fields.ItemSearchCategory.fields.Name,
        itemSearchCategory: {
          id: result.fields.ItemSearchCategory.row_id,
          name: result.fields.ItemSearchCategory.fields.Name,
        },
        levelItem: result.fields.LevelItem.row_id,
        name: result.fields.Name,
        rarity: result.fields.Rarity,
      }))
      .sort((a, b) => b.levelItem - a.levelItem),
  };
}
